import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { extractErrorMessage, useInvalidateQuery } from "@platform/api";
import {
  type CaseRunOut,
  deleteCaseRunMutation,
  listCaseRunsOptions,
} from "@platform/api/golden";
import {
  Alert,
  Button,
  Card,
  ConfirmationModal,
  Link,
} from "@platform/design-system";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router";

import { useGoldenClient } from "../api/GoldenClientProvider";
import { GOLDEN_TAGS } from "../api/goldenTags";
import { BACKGROUND_POLL_MS } from "../api/polling";
import { RfpLink } from "../shared/RfpLink";
import { formatTimestamp } from "../shared/values";

import { SectionStatus } from "./SectionStatus";

import type { ReactNode } from "react";

export const CASE_RUNS_TEST_ID = "golden-case-runs";

// The API refuses a suite run, which its suite's report keeps, and a run still queued
// or running, which its workflow would go on writing to.
const UNFINISHED_RUN_STATES: ReadonlySet<CaseRunOut["state"]> = new Set([
  "queued",
  "running",
]);
const isUnfinished = (run: CaseRunOut): boolean =>
  UNFINISHED_RUN_STATES.has(run.state);
const isDeletableRun = (run: CaseRunOut): boolean =>
  run.suite_id == null && !isUnfinished(run);

const unfinishedRunIds = (
  runs: readonly CaseRunOut[] | undefined,
): ReadonlySet<number> =>
  new Set((runs ?? []).filter(isUnfinished).map((run) => run.id));

// A finished run scores the case: it can draw the audit sample, move the case's state,
// and open discrepancies whose verdicts become corrections.
const useRefreshWhenRunsFinish = (
  runs: readonly CaseRunOut[] | undefined,
): void => {
  const invalidateQuery = useInvalidateQuery();
  const unfinished = useRef(unfinishedRunIds(runs));

  // Syncs the cache with a run that finished on the server.
  useEffect(() => {
    if (!runs) return;
    const before = unfinished.current;
    const now = unfinishedRunIds(runs);
    unfinished.current = now;
    const someRunFinished = [...before].some((id) => !now.has(id));
    if (!someRunFinished) return;
    for (const tags of [
      GOLDEN_TAGS.reviews,
      GOLDEN_TAGS.corrections,
      GOLDEN_TAGS.cases,
    ]) {
      void invalidateQuery({ tags: [...tags] });
    }
  }, [runs, invalidateQuery]);
};

/** The case's runs, newest first, each with the replay RFP RUMI filled in. */
export const CaseRuns = ({
  caseId,
}: {
  readonly caseId: number;
}): ReactNode => {
  const client = useGoldenClient();
  const invalidateQuery = useInvalidateQuery();
  // The API answers once a run's workflow starts, so the runs are polled while any is
  // unfinished, and what a finished run produces is fetched again when one finishes.
  const runs = useQuery({
    ...listCaseRunsOptions({ client, path: { case_id: caseId } }),
    refetchInterval: (query) =>
      query.state.data?.some(isUnfinished) ? BACKGROUND_POLL_MS : false,
  });
  useRefreshWhenRunsFinish(runs.data);
  const [confirmingRunId, setConfirmingRunId] = useState<number>();
  // Deleting the run that holds the audit sample starts the review over.
  const deleteRun = useMutation({
    ...deleteCaseRunMutation({ client }),
    onSuccess: () =>
      Promise.all([
        invalidateQuery({ tags: [...GOLDEN_TAGS.runs] }),
        invalidateQuery({ tags: [...GOLDEN_TAGS.reviews] }),
      ]),
  });

  return (
    <Card data-testid={CASE_RUNS_TEST_ID} title="Runs">
      {deleteRun.isSuccess && (
        <Alert data-testid={`${CASE_RUNS_TEST_ID}-notice`} severity="success">
          Run #{deleteRun.variables.path.run_id} deleted.
        </Alert>
      )}
      {deleteRun.isError && (
        <Alert
          data-testid={`${CASE_RUNS_TEST_ID}-delete-error`}
          severity="error"
        >
          {extractErrorMessage(deleteRun.error, "Failed to delete the run")}
        </Alert>
      )}
      {runs.isSuccess ? (
        <Table size="small" aria-label="Runs">
          <TableHead>
            <TableRow>
              <TableCell>Run</TableCell>
              <TableCell>State</TableCell>
              <TableCell>RFP RUMI filled</TableCell>
              <TableCell>Finished</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.data.map((run) => (
              <TableRow
                key={run.id}
                data-testid={`${CASE_RUNS_TEST_ID}-row-${run.id}`}
              >
                <TableCell>
                  <Link
                    component={RouterLink}
                    to={`/cases/${caseId}/runs/${run.id}`}
                  >
                    #{run.id}
                  </Link>
                  {run.suite_id != null && ` (suite ${run.suite_id})`}
                </TableCell>
                <TableCell title={run.error_message ?? undefined}>
                  {run.state}
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>
                  <RfpLink id={run.replay_rfp_id} url={run.replay_rfp_url} />
                </TableCell>
                <TableCell>{formatTimestamp(run.finished_at)}</TableCell>
                <TableCell>
                  {isDeletableRun(run) && (
                    <Button
                      variant="tertiary"
                      size="sm"
                      data-testid={`${CASE_RUNS_TEST_ID}-delete-${run.id}`}
                      disabled={deleteRun.isPending}
                      onClick={() => setConfirmingRunId(run.id)}
                    >
                      Delete
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {runs.data.length === 0 && (
              <TableRow data-testid={`${CASE_RUNS_TEST_ID}-empty`}>
                <TableCell colSpan={5}>No runs yet.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      ) : (
        <SectionStatus
          testId={CASE_RUNS_TEST_ID}
          label="runs"
          error={runs.error}
        />
      )}
      <ConfirmationModal
        open={confirmingRunId !== undefined}
        title={`Delete run #${confirmingRunId}?`}
        description="Its discrepancies leave the review queue, and if it holds the audit sample, the review starts over with a new sample."
        confirmButtonText="Delete run"
        cancelButtonText="Keep it"
        onCancel={() => setConfirmingRunId(undefined)}
        onAccept={() => {
          if (confirmingRunId === undefined) return;
          deleteRun.mutate({
            path: { case_id: caseId, run_id: confirmingRunId },
          });
          setConfirmingRunId(undefined);
        }}
      />
    </Card>
  );
};
